import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import {
    ArrowUpRight, ArrowDownRight, Calendar, CreditCard
} from 'lucide-react'
import { useLanguageStore, useCurrencyStore } from '../../lib/store'
import { formatCurrency } from '../../lib/utils'

export default function WalletPage() {
    useTranslation()
    const { language } = useLanguageStore()
    useCurrencyStore()

    const [filter, setFilter] = useState<'all' | 'deposits' | 'payments' | 'refunds'>('all')

    // Mock data
    const wallet = {
        balance: 2500000,
        totalDeposits: 5000000,
        totalSpent: 2500000,
    }

    const transactions = [
        { id: 1, type: 'payment', description: 'Payment for Honda Air Blade', amount: -400000, date: '2026-01-06', status: 'completed', bookingId: 1 },
        { id: 2, type: 'deposit', description: 'Deposit from Visa *4242', amount: 1000000, date: '2026-01-05', status: 'completed' },
        { id: 3, type: 'refund', description: 'Refund for cancelled booking #23', amount: 300000, date: '2026-01-03', status: 'completed' },
        { id: 4, type: 'payment', description: 'Payment for Toyota Vios', amount: -1600000, date: '2026-01-02', status: 'completed', bookingId: 2 },
        { id: 5, type: 'deposit', description: 'Deposit from MoMo', amount: 2000000, date: '2026-01-01', status: 'completed' },
    ]

    const filteredTransactions = filter === 'all'
        ? transactions
        : transactions.filter(t => t.type === filter.slice(0, -1)) // Remove 's' from filter

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold">{language === 'en' ? 'My Wallet' : 'Ví Của Tôi'}</h1>
                    <p className="text-gray-600">{language === 'en' ? 'Manage your balance and transactions' : 'Quản lý số dư và giao dịch'}</p>
                </div>

                {/* Balance Card */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-xl p-6 text-white mb-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div className="mb-4 md:mb-0">
                            <p className="opacity-80 mb-1">{language === 'en' ? 'Current Balance' : 'Số dư hiện tại'}</p>
                            <p className="text-4xl font-bold">{formatCurrency(wallet.balance, 'VND')}</p>
                        </div>
                        <div className="flex gap-3">
                            <button className="px-6 py-3 bg-white text-primary-600 rounded-lg font-bold hover:bg-gray-100 transition">
                                <CreditCard className="w-5 h-5 inline mr-2" />
                                {language === 'en' ? 'Add Funds' : 'Nạp tiền'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="card">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <ArrowDownRight className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Total Deposits' : 'Tổng nạp'}</p>
                                <p className="text-xl font-bold text-green-600">{formatCurrency(wallet.totalDeposits, 'VND')}</p>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                <ArrowUpRight className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm">{language === 'en' ? 'Total Spent' : 'Tổng chi'}</p>
                                <p className="text-xl font-bold text-red-600">{formatCurrency(wallet.totalSpent, 'VND')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Transactions */}
                <div className="card">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold">{language === 'en' ? 'Transaction History' : 'Lịch Sử Giao Dịch'}</h2>
                    </div>

                    {/* Filter Tabs */}
                    <div className="flex gap-2 mb-4 overflow-x-auto">
                        {(['all', 'deposits', 'payments', 'refunds'] as const).map((type) => (
                            <button
                                key={type}
                                onClick={() => setFilter(type)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${filter === type
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 hover:bg-gray-200'
                                    }`}
                            >
                                {type === 'all' && (language === 'en' ? 'All' : 'Tất cả')}
                                {type === 'deposits' && (language === 'en' ? 'Deposits' : 'Nạp tiền')}
                                {type === 'payments' && (language === 'en' ? 'Payments' : 'Thanh toán')}
                                {type === 'refunds' && (language === 'en' ? 'Refunds' : 'Hoàn tiền')}
                            </button>
                        ))}
                    </div>

                    {/* Transaction List */}
                    <div className="space-y-3">
                        {filteredTransactions.map((tx) => (
                            <div key={tx.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tx.type === 'deposit' ? 'bg-green-100' :
                                        tx.type === 'refund' ? 'bg-blue-100' : 'bg-red-100'
                                        }`}>
                                        {tx.type === 'deposit' && <ArrowDownRight className="w-5 h-5 text-green-600" />}
                                        {tx.type === 'payment' && <ArrowUpRight className="w-5 h-5 text-red-600" />}
                                        {tx.type === 'refund' && <ArrowDownRight className="w-5 h-5 text-blue-600" />}
                                    </div>
                                    <div>
                                        <p className="font-medium">{tx.description}</p>
                                        <p className="text-sm text-gray-500 flex items-center gap-2">
                                            <Calendar className="w-3 h-3" />
                                            {tx.date}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className={`font-bold text-lg ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {tx.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(tx.amount), 'VND')}
                                    </p>
                                    <span className="text-xs text-gray-500">{tx.status}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredTransactions.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                            {language === 'en' ? 'No transactions found' : 'Không có giao dịch'}
                        </div>
                    )}
                </div>

                {/* Payment Methods */}
                <div className="card mt-8">
                    <h2 className="text-xl font-bold mb-4">{language === 'en' ? 'Payment Methods' : 'Phương Thức Thanh Toán'}</h2>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <CreditCard className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="font-medium">Visa **** 4242</p>
                                    <p className="text-sm text-gray-500">{language === 'en' ? 'Expires' : 'Hết hạn'} 12/2027</p>
                                </div>
                            </div>
                            <span className="badge badge-success">{language === 'en' ? 'Default' : 'Mặc định'}</span>
                        </div>
                        <button className="w-full p-4 border-2 border-dashed rounded-lg text-gray-500 hover:border-primary-500 hover:text-primary-600 transition">
                            + {language === 'en' ? 'Add Payment Method' : 'Thêm phương thức thanh toán'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
